import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import { useLocalization } from "../../../context/LocalizeContextProvider";
import PillItem from "../PillItem";
import Table from "../Table";
import MedicalInfoSection from "../MedicalInfoSection"; // Import the new component
import styles from "./AccordionHistory.module.css";
import { getDetailVisitPayment } from "../../../services/Handlers/PaymentService";
import { typeDiscountObj } from "../../../utils/constants";
import { formatNumberWithCurrency } from "../../../utils/utils";

const WrapperInformation = ({ label = "", value }) => {
  const isJsx = typeof value === "object" && value !== null;

  return (
    <div className={styles.informationWrapper}>
      <p className={styles.infoLabel}>{label}</p>
      {isJsx ? value : <p className={styles.infoValue}>{value || "-"}</p>}
    </div>
  );
};

const AccordionHistory = ({
  visitId,
  date,
  diagnoses = [],
  doctorName,
  anamnesis,
  physicalExamination,
  uploadedFiles = [],
  onClickDetail = () => undefined,
}) => {
  const localize = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [dataPayment, setDataPayment] = useState({
    datePayment: "",
    paymentMethod: "",
    addedBy: "",
    totalPayment: "",
  });

  const { isLoading: isLoadingDetail } = useQuery(
    ["DetailVisitPatient", visitId],
    () => getDetailVisitPayment(visitId),
    {
      enabled: !!visitId && isOpen,
      onSuccess: (res) => {
        if (res?.status === 200) {
          const response = res?.data;
          if (response) {
            setDataPayment({
              datePayment: response?.payment_date
                ? moment(response?.payment_date, "DD-MM-YYYY").format(
                    "DD MMM YYYY"
                  )
                : "-",
              paymentMethod: response?.payment_method || "-",
              addedBy: response?.created_by || "-",
              totalPayment: formatNumberWithCurrency(
                response?.total_amount || 0
              ),
            });
            if (response?.items?.length > 0) {
              const processedItem = (response?.items || []).map((item) => {
                let historySubTotal = item.price || 0;

                if (
                  item.type_discount === typeDiscountObj.NUMBER &&
                  item.discount > 0
                ) {
                  historySubTotal -= item.discount;
                } else if (
                  item.type_discount === typeDiscountObj.PERCENTAGE &&
                  item.discount > 0
                ) {
                  historySubTotal -=
                    (parseFloat(item.discount) / 100) * historySubTotal;
                }

                return {
                  ...item,
                  sub_total: historySubTotal,
                  formatted_sub_total:
                    formatNumberWithCurrency(historySubTotal),
                  formatted_amount: formatNumberWithCurrency(item.price || 0),
                  formatted_discount:
                    item.type_discount === typeDiscountObj.NUMBER
                      ? formatNumberWithCurrency(item.discount)
                      : item.discount
                      ? `${item.discount}%`
                      : "-",
                };
              });
              setItems(processedItem);
            }
          }
        }
      },
    }
  );

  const columnsItem = [
    {
      label: localize.getText("nameItem"),
      key: "name_item",
      style: { fontSize: 14 },
    },
    {
      label: localize.getText("priceItem"),
      key: "price_item",
      style: { fontSize: 14 },
    },
    {
      label: localize.getText("quantity"),
      key: "quantity",
      style: { fontSize: 14 },
    },
    {
      label: localize.getText("discount"),
      key: "discount",
      style: { fontSize: 14 },
    },
    {
      label: localize.getText("subTotal"),
      key: "sub_total",
      style: { fontSize: 14 },
    },
  ];

  const MAX_DISPLAY_DIAGNOSES = 3;
  const displayDiagnoses = diagnoses.slice(0, MAX_DISPLAY_DIAGNOSES);
  const remainingCount = diagnoses.length - MAX_DISPLAY_DIAGNOSES;

  return (
    <div className={styles.container}>
      <div
        className={`${styles.header} ${isOpen ? styles.headerOpen : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.headerLeft}>
          <p className={styles.headerDate}>{date}</p>
          <div className={styles.pillsContainer}>
            {diagnoses.length > 0 ? (
              <>
                {displayDiagnoses.map((diagnosis, index) => (
                  <PillItem key={index} text={diagnosis} />
                ))}
                {remainingCount > 0 && <PillItem text={`+${remainingCount}`} />}
              </>
            ) : (
              <p className={styles.noData}>{localize.getText("noDiagnosis")}</p>
            )}
          </div>
        </div>
        <i
          className={`${isOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"} ${
            styles.headerIcon
          }`}
        ></i>
      </div>

      {isOpen && (
        <div className={styles.content}>
          <MedicalInfoSection
            doctorName={doctorName}
            anamnesis={anamnesis}
            physicalExamination={physicalExamination}
            diagnoses={diagnoses}
            uploadedFiles={uploadedFiles}
            onClickDetail={onClickDetail}
            showDetailLink={true}
          />

          <div className={styles.paymentInfo}>
            <p className={styles.sectionTitle}>{localize.getText("payment")}</p>
            <Row>
              <Col className={styles.paymentCol}>
                <WrapperInformation
                  label={localize.getText("datePayment")}
                  value={dataPayment.datePayment}
                />
                <WrapperInformation
                  label={localize.getText("paymentMethod")}
                  value={dataPayment.paymentMethod}
                />
              </Col>
              <Col className={styles.paymentCol}>
                <WrapperInformation
                  label={localize.getText("createdBy")}
                  value={dataPayment.addedBy}
                />
              </Col>
              <Col className={styles.paymentCol}>
                <WrapperInformation
                  label={localize.getText("totalPayment")}
                  value={dataPayment.totalPayment}
                />
              </Col>
            </Row>
            <Table
              columns={columnsItem}
              totalEntries={items.length}
              totalPage={1}
              totalShow={items.length}
              loading={isLoadingDetail}
              noPagination
            >
              {items.map((item, index) => (
                <tr key={index}>
                  <td>{item.name || "-"}</td>
                  <td>{item.formatted_amount || "-"}</td>
                  <td>{item.quantity || "-"}</td>
                  <td>{item.formatted_discount || "-"}</td>
                  <td>{item.formatted_sub_total || "-"}</td>
                </tr>
              ))}
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccordionHistory;
